import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { weeklySlotApiService, WeeklySlot } from '../../services/weeklySlotApi';
import { scheduleExceptionApiService, UnavailableDate } from '../../services/scheduleExceptionApi';
import { appointmentApiService, AppointmentResponse } from '../../services/appointmentApi';
import BookingHeader from '../components/BookingHeader';
import IrisMessage from '../components/IrisMessage';
import CalendarView from '../components/CalendarView';
import TimeSlotSelector from '../components/TimeSlotSelector';
import AppointmentForm from '../components/AppointmentForm';
import ConfirmationView from '../components/ConfirmationView';

interface AppointmentData {
  subject: string;
  message: string;
  date: string;
  time: string;
  appointmentType: string;
}

const BookingAppointmentPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);
  const [weeklySlots, setWeeklySlots] = useState<WeeklySlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [unavailableDates, setUnavailableDates] = useState<UnavailableDate[]>([]);
  const [loadingExceptions, setLoadingExceptions] = useState(true);
  
  const [appointmentData, setAppointmentData] = useState<AppointmentData>({
    subject: '',
    message: '',
    date: '',
    time: '',
    appointmentType: ''
  });
  
  const [createdAppointment, setCreatedAppointment] = useState<AppointmentResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Charger les créneaux et exceptions
  useEffect(() => {
    const loadData = async () => {
      try {
        const [slotsPromise, exceptionsPromise] = await Promise.allSettled([
          (async () => {
            setLoadingSlots(true);
            console.log('🔄 Chargement des créneaux hebdomadaires...');
            
            const slots = await weeklySlotApiService.getWeeklySlots();
            console.log('📅 Créneaux récupérés:', slots);
            
            const activeSlots = slots.filter(slot => slot.is_active);
            console.log('✅ Créneaux actifs:', activeSlots);
            
            setWeeklySlots(activeSlots);
            
            if (activeSlots.length === 0) {
              console.warn('⚠️ Aucun créneau actif configuré');
            }
          })(),
          
          (async () => {
            setLoadingExceptions(true);
            console.log('🔄 Chargement des exceptions de planning...');
            
            const exceptions = await scheduleExceptionApiService.getUnavailableDates();
            console.log('🚫 Dates indisponibles récupérées:', exceptions);
            
            setUnavailableDates(exceptions);
          })()
        ]);

        if (slotsPromise.status === 'rejected') {
          console.error('❌ Erreur chargement créneaux:', slotsPromise.reason);
          setWeeklySlots([]);
        }

        if (exceptionsPromise.status === 'rejected') {
          console.error('❌ Erreur chargement exceptions:', exceptionsPromise.reason);
          setUnavailableDates([]);
        }

      } finally {
        setLoadingSlots(false);
        setLoadingExceptions(false);
      }
    };

    loadData();
  }, []);

  const handleInputChange = (field: keyof AppointmentData, value: string) => {
    setAppointmentData(prev => ({ ...prev, [field]: value }));
  };

  const handleDateSelect = (fullDate: string) => {
    setSelectedDate(fullDate);
    setSelectedTime('');
    setAppointmentData(prev => ({ ...prev, date: fullDate, time: '', appointmentType: '' }));
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setAppointmentData(prev => ({ ...prev, time, appointmentType: '' }));
  };

  const handleAppointmentTypeSelect = (type: string) => {
    setAppointmentData(prev => ({ ...prev, appointmentType: type }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      
      const apiData = appointmentApiService.prepareAppointmentData(appointmentData);
      
      const errors = appointmentApiService.validateAppointmentData(apiData);
      if (errors.length > 0) {
        setSubmitError('Erreurs de validation:\n' + errors.join('\n'));
        return;
      }
      
      const response = await appointmentApiService.createAppointment(apiData);
      
      setCreatedAppointment(response);
      setSubmitted(true);
      
      console.log('✅ Rendez-vous créé avec succès:', response);
      
    } catch (error: any) {
      console.error('❌ Erreur création rendez-vous:', error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  if (submitted) {
    return (
      <ConfirmationView
        appointmentData={appointmentData}
        createdAppointment={createdAppointment}
        onBack={handleBack}
      />
    );
  }

  return (
    <div className="min-h-screen bg-ice">
      <BookingHeader 
        step={step} 
        totalSteps={2} 
        onBack={handleBack} 
      />

      <div className="container-max py-8">
        <div className="max-w-6xl mx-auto">
          
          <IrisMessage step={step} totalSteps={2} />

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-8"
              >
                <CalendarView
                  selectedDate={selectedDate}
                  onDateSelect={handleDateSelect}
                  unavailableDates={unavailableDates}
                />

                <div className="space-y-4">
                  <TimeSlotSelector
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    selectedType={appointmentData.appointmentType}
                    weeklySlots={weeklySlots}
                    loadingSlots={loadingSlots}
                    isAuthenticated={isAuthenticated}
                    onTimeSelect={handleTimeSelect}
                    onTypeSelect={handleAppointmentTypeSelect}
                  />
                  
                  <div className="flex justify-end mt-8">
                    <button
                      onClick={() => setStep(2)}
                      disabled={!isAuthenticated || !selectedDate || !selectedTime || !appointmentData.appointmentType}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      title={!isAuthenticated ? 'Veuillez vous connecter pour continuer' : ''}
                    >
                      Continuer
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <AppointmentForm
                appointmentData={appointmentData}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onInputChange={handleInputChange}
                onSubmit={handleSubmit}
                onBack={() => setStep(1)}
                onClearError={() => setSubmitError(null)}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default BookingAppointmentPage;
